import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Flag, 
  Calendar as CalendarIcon, 
  Clock,
  MapPin,
  Loader2,
  Search
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CreateRoundPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    type: 'casual',
    course_id: '',
    course_name: '',
    tee_time_date: null,
    tee_time_hour: '08',
    tee_time_minute: '00',
    holes: 18,
    format: 'stroke',
    visibility: 'members',
    lock_policy: 'none'
  });

  useEffect(() => {
    if (searchQuery.length >= 2) {
      searchCourses();
    }
  }, [searchQuery]);

  const searchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses?search=${searchQuery}&limit=10`);
      setCourses(response.data.courses);
    } catch (error) {
      console.error('Failed to search courses:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.course_id) {
      toast.error('Please select a course');
      return;
    }
    if (!formData.tee_time_date) {
      toast.error('Please select a tee time date');
      return;
    }

    setLoading(true);
    try {
      const teeTime = new Date(formData.tee_time_date);
      teeTime.setHours(parseInt(formData.tee_time_hour), parseInt(formData.tee_time_minute));

      const response = await axios.post(`${API_URL}/rounds`, {
        type: formData.type,
        course_id: formData.course_id,
        tee_time: teeTime.toISOString(),
        holes: formData.holes,
        format: formData.format,
        visibility: formData.visibility,
        lock_policy: formData.lock_policy
      });

      toast.success('Round created!');
      navigate(`/rounds/${response.data.round_id}`);
    } catch (error) {
      toast.error('Failed to create round');
    } finally {
      setLoading(false);
    }
  };

  const roundTypes = [
    { value: 'casual', label: 'Casual Round' },
    { value: 'league', label: 'League Play' },
    { value: 'tournament', label: 'Tournament' }
  ];

  const formats = [
    { value: 'stroke', label: 'Stroke Play' },
    { value: 'stableford', label: 'Stableford' },
    { value: 'match', label: 'Match Play' },
    { value: 'scramble', label: 'Scramble' }
  ];

  const lockPolicies = [
    { value: 'none', label: 'No Confirmation', desc: 'Scores can be edited anytime' },
    { value: 'single_confirm', label: 'Single Confirm', desc: '1 player must confirm to lock' },
    { value: 'two_confirm', label: 'Two Confirm', desc: '2 players must confirm to lock' }
  ];

  const hours = Array.from({ length: 14 }, (_, i) => String(i + 6).padStart(2, '0'));
  const minutes = ['00', '15', '30', '45'];

  return (
    <div className="max-w-2xl mx-auto" data-testid="create-round-page">
      <h1 className="font-display font-bold text-2xl text-white mb-6">Create New Round</h1>

      <form onSubmit={handleSubmit}>
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-6 space-y-6">
            {/* Round Type */}
            <div className="space-y-3">
              <Label className="text-cream/90">Round Type</Label>
              <div className="grid grid-cols-3 gap-3">
                {roundTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, type: type.value })}
                    className={cn(
                      "p-3 rounded-sm border text-center transition-colors",
                      formData.type === type.value
                        ? "bg-gold border-gold text-deep-green"
                        : "bg-white/5 border-white/20 text-white hover:border-gold/50"
                    )}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Course Search */}
            <div className="space-y-3">
              <Label className="text-cream/90">Course</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/50" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for a course..."
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  data-testid="course-search"
                />
              </div>
              {courses.length > 0 && searchQuery && (
                <div className="border border-white/20 rounded-sm bg-deep-green max-h-48 overflow-y-auto">
                  {courses.map((course) => (
                    <button
                      key={course.course_id}
                      type="button"
                      onClick={() => {
                        setFormData({ 
                          ...formData, 
                          course_id: course.course_id,
                          course_name: course.name 
                        });
                        setSearchQuery('');
                        setCourses([]);
                      }}
                      className="w-full p-3 text-left hover:bg-white/10 transition-colors"
                      data-testid={`course-option-${course.course_id}`}
                    >
                      <p className="text-white font-semibold">{course.name}</p>
                      <p className="text-cream/60 text-sm">{course.city}, {course.state}</p>
                    </button>
                  ))}
                </div>
              )}
              {formData.course_name && (
                <div className="p-3 bg-gold/20 rounded-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gold" />
                  <span className="text-white">{formData.course_name}</span>
                </div>
              )}
            </div>

            {/* Tee Time */}
            <div className="space-y-3">
              <Label className="text-cream/90">Tee Time</Label>
              <div className="flex gap-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className={cn(
                        "flex-1 justify-start text-left font-normal bg-white/10 border-white/20 text-white",
                        !formData.tee_time_date && "text-white/50"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.tee_time_date 
                        ? format(formData.tee_time_date, 'MMM d, yyyy')
                        : 'Select date'
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.tee_time_date}
                      onSelect={(date) => setFormData({ ...formData, tee_time_date: date })}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <select
                  value={formData.tee_time_hour}
                  onChange={(e) => setFormData({ ...formData, tee_time_hour: e.target.value })}
                  className="h-10 px-3 rounded-sm bg-white/10 border border-white/20 text-white"
                >
                  {hours.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
                <span className="text-white self-center">:</span>
                <select
                  value={formData.tee_time_minute}
                  onChange={(e) => setFormData({ ...formData, tee_time_minute: e.target.value })}
                  className="h-10 px-3 rounded-sm bg-white/10 border border-white/20 text-white"
                >
                  {minutes.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Holes & Format */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <Label className="text-cream/90">Holes</Label>
                <div className="flex gap-2">
                  {[9, 18].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setFormData({ ...formData, holes: num })}
                      className={cn(
                        "flex-1 p-3 rounded-sm border transition-colors",
                        formData.holes === num
                          ? "bg-gold border-gold text-deep-green"
                          : "bg-white/5 border-white/20 text-white hover:border-gold/50"
                      )}
                    >
                      {num} Holes
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <Label className="text-cream/90">Format</Label>
                <select
                  value={formData.format}
                  onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                  className="w-full h-10 px-4 rounded-sm bg-white/10 border border-white/20 text-white"
                  data-testid="format-select"
                >
                  {formats.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Lock Policy */}
            <div className="space-y-3">
              <Label className="text-cream/90">Score Confirmation</Label>
              <div className="space-y-2">
                {lockPolicies.map((policy) => (
                  <button
                    key={policy.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, lock_policy: policy.value })}
                    className={cn(
                      "w-full p-3 rounded-sm border text-left transition-colors",
                      formData.lock_policy === policy.value
                        ? "bg-gold border-gold text-deep-green"
                        : "bg-white/5 border-white/20 text-white hover:border-gold/50"
                    )}
                  >
                    <p className="font-semibold">{policy.label}</p>
                    <p className={cn(
                      "text-sm",
                      formData.lock_policy === policy.value ? "text-deep-green/70" : "text-cream/60"
                    )}>
                      {policy.desc}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Submit */}
            <Button 
              type="submit"
              className="w-full btn-gold py-6"
              disabled={loading}
              data-testid="create-round-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Round...
                </>
              ) : (
                <>
                  <Flag className="w-4 h-4 mr-2" />
                  Create Round
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default CreateRoundPage;
